import React, { useState, useEffect, useRef } from 'react';
import { Building2, RefreshCw, Trash2, AlertCircle, Loader2, Link2 } from 'lucide-react';
import { apiService, PlaidIntegration } from '../services/api';
import { Button } from './ui/button';
import { PlaidLinkButton } from './PlaidLinkButton';
import { PlaidReconnectButton } from './PlaidReconnectButton';
import { Label } from './ui/label';

interface BankConnectionManagerProps {
  onConnectionChange?: () => void;
}

export const BankConnectionManager: React.FC<BankConnectionManagerProps> = ({
  onConnectionChange,
}) => {
  const [integrations, setIntegrations] = useState<PlaidIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // Hide disconnected connections from the Settings UI
  const visibleIntegrations = integrations.filter((i) => i.status !== 'disconnected');

  useEffect(() => {
    const controller = new AbortController();
    fetchIntegrations(false, controller.signal);

    return () => {
      // Cancel any in-flight request and bump request id so stale responses are ignored
      controller.abort();
      requestIdRef.current += 1;
    };
  }, []);

  const fetchIntegrations = async (isRefresh = false, signal?: AbortSignal) => {
    const requestId = ++requestIdRef.current;
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await apiService.getPlaidIntegrationsOnce({
        signal,
        force: isRefresh,
      });
      // Only update state if this is the latest request
      if (requestIdRef.current === requestId) {
        setIntegrations(response.integrations);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bank connections');
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchIntegrations(true);
  };

  const handleConnectionSuccess = async () => {
    await fetchIntegrations(true);
    if (onConnectionChange) {
      onConnectionChange();
    }
  };

  const handleSync = async (integrationId: string) => {
    try {
      setSyncing(integrationId);
      setError(null);
      await apiService.syncPlaidTransactions(integrationId);
      if (onConnectionChange) {
        onConnectionChange();
      }
      // Refresh integrations to get updated categorization status
      await fetchIntegrations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync transactions');
    } finally {
      setSyncing(null);
    }
  };

  const handleDeleteConnection = async (integrationId: string, institutionName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${institutionName}?`)) {
      return;
    }

    try {
      setDeleting(integrationId);
      setError(null);
      await apiService.disconnectPlaidIntegration(integrationId);
      await fetchIntegrations(true);
      if (onConnectionChange) {
        onConnectionChange();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete bank connection');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (integration: PlaidIntegration) => {
    // Show categorization status if actively categorizing
    if (integration.is_categorizing) {
      const totalJobs = integration.categorization_pending + integration.categorization_processing;
      return (
        <span 
          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full gap-1"
          style={{ 
            backgroundColor: 'color-mix(in srgb, var(--theme-accent) 15%, transparent)',
            color: 'var(--theme-accent)'
          }}
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          Categorizing {totalJobs} transaction{totalJobs !== 1 ? 's' : ''}
        </span>
      );
    }
    
    // Otherwise show regular status
    switch (integration.status) {
      case 'active':
        return (
          <span 
            className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-income) 15%, transparent)',
              color: 'var(--theme-income)'
            }}
          >
            Active
          </span>
        );
      case 'error':
        return (
          <span 
            className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full gap-1"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-expense) 15%, transparent)',
              color: 'var(--theme-expense)'
            }}
          >
            <AlertCircle className="h-3 w-3" />
            Error
          </span>
        );
      case 'disconnected':
        return (
          <span 
            className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
              color: 'var(--theme-text-secondary)'
            }}
          >
            Disconnected
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="apple-card !p-0 overflow-hidden">
        <div 
          className="px-5 py-4"
          style={{ borderBottom: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
        >
          <Label 
            className="text-base font-semibold"
            style={{ color: 'var(--theme-text)' }}
          >
            Bank Connections
          </Label>
        </div>
        <div 
          className="text-center py-8"
          style={{ color: 'var(--theme-text-secondary)' }}
        >
          Loading connections...
        </div>
      </div>
    );
  }

  return (
    <div className="apple-card !p-0 overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
      >
        <Label 
          className="text-base font-semibold"
          style={{ color: 'var(--theme-text)' }}
        >
          Bank Connections
        </Label>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="gap-2"
            style={{
              backgroundColor: 'var(--theme-surface)',
              borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
              color: 'var(--theme-text)'
            }}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <PlaidLinkButton
            onSuccess={handleConnectionSuccess}
            onError={(err) => setError(err)}
            variant="default"
            size="sm"
          >
            Add Bank Account
          </PlaidLinkButton>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 py-4">
        {error && (
          <div 
            className="px-4 py-3 rounded-xl mb-4 text-sm"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, transparent)',
              color: 'var(--theme-expense)'
            }}
          >
            {error}
          </div>
        )}

        {visibleIntegrations.length === 0 ? (
          <div className="text-center py-8">
            <div 
              className="inline-flex p-4 rounded-2xl mb-4"
              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
            >
              <Building2 className="h-10 w-10" style={{ color: 'var(--theme-text-secondary)' }} />
            </div>
            <p 
              className="mb-4 text-sm"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              No bank accounts connected yet
            </p>
            <PlaidLinkButton
              onSuccess={handleConnectionSuccess}
              onError={(err) => setError(err)}
              variant="outline"
              size="default"
            >
              Connect Your First Bank
            </PlaidLinkButton>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleIntegrations.map((integration) => (
              <div
                key={integration.id}
                className="rounded-xl p-4 transition-all"
                style={{ 
                  backgroundColor: 'var(--theme-background)',
                  border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="p-2.5 rounded-xl"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' }}
                  >
                    <Building2 className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="font-semibold text-sm mb-1"
                      style={{ color: 'var(--theme-text)' }}
                    >
                      {integration.institution_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {getStatusBadge(integration)}
                      <span 
                        className="text-xs"
                        style={{ color: 'var(--theme-text-secondary)' }}
                      >
                        Connected on {new Date(integration.created_at || '').toLocaleDateString()}
                      </span>
                    </div>
                    {integration.status === 'active' && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => handleSync(integration.id)}
                          disabled={syncing === integration.id}
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          style={{
                            backgroundColor: 'var(--theme-surface)',
                            borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                            color: 'var(--theme-text)'
                          }}
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${syncing === integration.id ? 'animate-spin' : ''}`}
                          />
                          Sync Now
                        </Button>
                        <Button
                          onClick={() =>
                            handleDeleteConnection(integration.id, integration.institution_name)
                          }
                          disabled={deleting === integration.id}
                          variant="ghost"
                          size="sm"
                          className="gap-2"
                          style={{ color: 'var(--theme-expense)' }}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    )}
                    {integration.status === 'error' && (
                      <div className="mt-2">
                        <p 
                          className="text-sm mb-3"
                          style={{ color: 'var(--theme-expense)' }}
                        >
                          There was an error syncing this account. Please reconnect to fix it.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <PlaidReconnectButton
                            integrationId={integration.id}
                            onSuccess={handleConnectionSuccess}
                            onError={(err) => setError(err)}
                            variant="default"
                            size="sm"
                            className="gap-2"
                          >
                            <Link2 className="h-4 w-4" />
                            Reconnect
                          </PlaidReconnectButton>
                          <Button
                            onClick={() =>
                              handleDeleteConnection(integration.id, integration.institution_name)
                            }
                            disabled={deleting === integration.id}
                            variant="ghost"
                            size="sm"
                            className="gap-2"
                            style={{ color: 'var(--theme-expense)' }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

